<template>
  <div>
    <exo-table :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Reaction Wheel Definition"
          >
            The reaction wheel is an actuator used to control the attitude of the
            spacecraft.
            <br />
            Here, the conservation of angular momentum (or Newton's third law)
            is used to produce a torque by changing the angular rate of the
            reaction wheel.
            <br />
            To correctly define this object, the user may define :
            <ul>
              <li>The position of the reaction wheel in spacecraft frame.</li>
              <li>The direction of angular rate in spacecraft frame.</li>
              <li>The reaction wheel maximum torque.</li>
              <li>The reaction wheel maximum angular momentum.</li>
              <li>The reaction wheel nominal capacity which represent the angular momentum of reaction wheel during nominal operations.
                It is define as percentage of maximum angular momentum.
              </li>
            </ul>
          </exo-help-dialog>
        </template>
        <exo-input-name :root="_self" />
        <v-row no-gutters justify="start">
          <v-col cols="4">
            <exo-input-double :root="_self" name="positionInSpacecraftFrameX" />
          </v-col>
          <v-col cols="4">
            <exo-input-double :root="_self" name="positionInSpacecraftFrameY" />
          </v-col>
          <v-col cols="4">
            <exo-input-double :root="_self" name="positionInSpacecraftFrameZ" />
          </v-col>
        </v-row>
        <v-row no-gutters justify="start">
          <v-col cols="4">
            <exo-input-double
              :root="_self"
              name="directionInSpacecraftFrameX"
            />
          </v-col>
          <v-col cols="4">
            <exo-input-double
              :root="_self"
              name="directionInSpacecraftFrameY"
            />
          </v-col>
          <v-col cols="4">
            <exo-input-double
              :root="_self"
              name="directionInSpacecraftFrameZ"
            />
          </v-col>
        </v-row>
        <exo-form-alert
          type="error"
          v-if="
            Number(inputs.directionInSpacecraftFrameX) === 0 &&
            Number(inputs.directionInSpacecraftFrameY) === 0 &&
            Number(inputs.directionInSpacecraftFrameZ) === 0
          "
        >
          The norm of the direction vector must be positive
        </exo-form-alert>
        <v-row no-gutters justify="start">
          <v-col cols="4">
            <exo-input-double :root="_self" name="maxAngularMomentum" />
          </v-col>
          <v-col cols="4">
            <exo-input-double :root="_self" name="nominalCapacity" />
          </v-col>
          <v-col cols="4">
            <exo-input-double :root="_self" name="maxTorque" />
          </v-col>
        </v-row>
        <exo-form-alert
          type="warning"
          v-if="
            currentParametricValue !== false && allowsParametricValues === false
          "
        >
          WARNING: parametric values are only supported in the Parametric
          Studies module. To use this object in the current module, please
          remove the range of values for '{{ currentParametricValue }}'.
        </exo-form-alert>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="this.modalRef" />
  </div>
</template>

<script>
import fieldsDescription from '@/tools/fieldsDescription'
import formMixin from '../../mixins/forms.js'
import analyticalMixin from '../../mixins/analyticalFoms.js'
import { mapGetters } from 'vuex'
// import numericalMixin from '../../mixins/numericalForms.js'

export default {
  name: 'reactionWheelDefinition',

  mixins: [formMixin, analyticalMixin],
  // mixins: [formMixin, numericalMixin],

  computed: {
    ...mapGetters(['currentMission']),

    currentParametricValue() {
      let listOfParametrics = this.inputs.parametricValues
      for (let nameOfParametric in listOfParametrics) {
        if (listOfParametrics[nameOfParametric].active === true) {
          return fieldsDescription[this.getCurrentFormType()].properties[
            nameOfParametric
          ].label
        }
      }
      return false
    },

    allowsParametricValues() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'ParametricStudyRequest'
      )
    }
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'positionInSpacecraftFrameX',
          text:
            'X position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameX'),
          sortable: true
        },
        {
          value: 'positionInSpacecraftFrameY',
          text:
            'Y position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameY'),
          sortable: true
        },
        {
          value: 'positionInSpacecraftFrameZ',
          text:
            'Z position' +
            this.getDefaultUnitLabel('positionInSpacecraftFrameZ'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameX',
          text:
            'X direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameX'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameY',
          text:
            'Y direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameY'),
          sortable: true
        },
        {
          value: 'directionInSpacecraftFrameZ',
          text:
            'Z direction' +
            this.getDefaultUnitLabel('directionInSpacecraftFrameZ'),
          sortable: true
        },
        {
          value: 'maxTorque',
          text: 'Maximum torque' + this.getDefaultUnitLabel('maxTorque'),
          sortable: true
        },
        {
          value: 'maxAngularMomentum',
          text:
            'Maximum angular Momentum' +
            this.getDefaultUnitLabel('maxAngularMomentum'),
          sortable: true
        },
        {
          value: 'nominalCapacity',
          text:
            'Nominal capacity' +
            this.getDefaultUnitLabel('nominalCapacity'),
          sortable: true
        },
        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,

      inputs: {
        positionInSpacecraftFrameX: this.getDefaultValue(
          'positionInSpacecraftFrameX'
        ),
        positionInSpacecraftFrameY: this.getDefaultValue(
          'positionInSpacecraftFrameY'
        ),
        positionInSpacecraftFrameZ: this.getDefaultValue(
          'positionInSpacecraftFrameZ'
        ),
        directionInSpacecraftFrameX: this.getDefaultValue(
          'directionInSpacecraftFrameX'
        ),
        directionInSpacecraftFrameY: this.getDefaultValue(
          'directionInSpacecraftFrameY'
        ),
        directionInSpacecraftFrameZ: this.getDefaultValue(
          'directionInSpacecraftFrameZ'
        ),
        maxTorque: this.getDefaultValue('maxTorque'),
        maxAngularMomentum: this.getDefaultValue('maxAngularMomentum'),
        nominalCapacity: this.getDefaultValue('nominalCapacity'),
        parametricValues: {
          // positionInSpacecraftFrameX: this.buildDefaultParametricValue(
          //   'positionInSpacecraftFrameX'
          // ),
          // positionInSpacecraftFrameY: this.buildDefaultParametricValue(
          //   'positionInSpacecraftFrameY'
          // ),
          // positionInSpacecraftFrameZ: this.buildDefaultParametricValue(
          //   'positionInSpacecraftFrameZ'
          // ),
          // directionInSpacecraftFrameX: this.buildDefaultParametricValue(
          //   'directionInSpacecraftFrameX'
          // ),
          // directionInSpacecraftFrameY: this.buildDefaultParametricValue(
          //   'directionInSpacecraftFrameY'
          // ),
          // directionInSpacecraftFrameZ: this.buildDefaultParametricValue(
          //   'directionInSpacecraftFrameZ'
          // ),
          // maxAngularMomentum:
          //   this.buildDefaultParametricValue('maxAngularMomentum'),
          // maxTorque: this.buildDefaultParametricValue('maxTorque')
        }
      }
    }
  },

  methods: {
    getCurrentFormType() {
      return 'ReactionWheelDefinition'
    },

    getRefList(param) {
      return []
    },

    getFieldState(parentObject, fieldName, required) {
      let fieldState = analyticalMixin.methods.getFieldState.call(
        this,
        parentObject,
        fieldName,
        required
      )
      // Check if norm is correct
      if (JSON.stringify(fieldName).includes("directionInSpacecraftFrame") && this.inputs.directionInSpacecraftFrameX == 0 && this.inputs.directionInSpacecraftFrameY == 0 && this.inputs.directionInSpacecraftFrameZ == 0
      ) {
          fieldState.value = false
          fieldState.message =
            'Sorry, the norm of the direction must be above 0.'
        }
      return fieldState
    }
  }
}
</script>
